/**
 * Cash Tracker — lightweight cash ledger on a launched development.
 * The approved P&L baseline is a frozen snapshot. Live scenario edits never
 * rewrite it. This is not accounting: four statuses, one item table.
 */

export const CASH_STATUSES = ['expected', 'due', 'paid', 'cancelled'];
export const CASH_DIRECTIONS = ['in', 'out'];

export const STATUS_LABEL = {
  expected: 'Expected',
  due: 'Due',
  paid: 'Paid',
  cancelled: 'Cancelled',
};

export const DIRECTION_LABEL = {
  in: 'In',
  out: 'Out',
};

export const ENTITY_ROLES = ['spv', 'developer', 'holdco', 'bank', 'other'];
export const ENTITY_ROLE_LABEL = {
  spv: 'SPV',
  developer: 'Developer',
  holdco: 'HoldCo',
  bank: 'Bank',
  other: 'Other',
};

/** Leaf P&L rows snapshotted at launch. Totals are derived, not stored as items.
 *  Funding rows (principal draws/repays) are ledger-only — not P&L Fin. Cost. */
export const BASELINE_CATEGORY_DEFS = [
  { key: 'salesRevenue', label: 'Sales', section: 'Revenue', kind: 'revenue' },
  { key: 'parkingRevenue', label: 'Parking', section: 'Revenue', kind: 'revenue' },
  { key: 'landCost', label: 'Terrain', section: 'Land', kind: 'cost' },
  { key: 'notaryFees', label: 'Notary', section: 'Land', kind: 'cost' },
  { key: 'otherLandFees', label: 'Other', section: 'Land', kind: 'cost' },
  { key: 'constructionCost', label: 'Build', section: 'Construction', kind: 'cost' },
  { key: 'constructionReserve', label: 'Reserve', section: 'Construction', kind: 'cost' },
  { key: 'architectureCost', label: 'Architecture', section: 'Soft', kind: 'cost' },
  { key: 'consultantsCost', label: 'Consultants', section: 'Soft', kind: 'cost' },
  { key: 'contingencyCost', label: 'Contingency', section: 'Soft', kind: 'cost' },
  { key: 'commercializationCost', label: 'Sales Cost', section: 'Commercial', kind: 'cost' },
  { key: 'pilotageCost', label: 'Pilotage', section: 'Commercial', kind: 'cost' },
  { key: 'feasibilityStudy', label: 'Feasibility', section: 'Capital', kind: 'cost' },
  { key: 'taxAmount', label: 'Tax', section: 'Tax', kind: 'cost' },
  // Interest & fees only (P&L). Principal belongs under Financing in/out.
  { key: 'totalFinancingCost', label: 'Fin. Cost (fees)', section: 'Financing', kind: 'cost' },
  { key: 'financingDraw', label: 'Financing in', section: 'Funding', kind: 'funding' },
  { key: 'financingRepayment', label: 'Financing out', section: 'Funding', kind: 'funding' },
  { key: 'other', label: 'Other / adjustment', section: 'Other', kind: 'other' },
];

const REVENUE_KEYS = ['salesRevenue', 'parkingRevenue'];
const LAND_KEYS = ['landCost', 'notaryFees', 'otherLandFees'];
const CONSTRUCTION_KEYS = ['constructionCost', 'constructionReserve'];
const SOFT_KEYS = ['architectureCost', 'consultantsCost', 'contingencyCost'];
const COMMERCIAL_KEYS = ['commercializationCost', 'pilotageCost'];
const DEV_COST_KEYS = [...LAND_KEYS, ...CONSTRUCTION_KEYS, ...SOFT_KEYS, ...COMMERCIAL_KEYS];
const FUNDING_KEYS = ['financingDraw', 'financingRepayment'];

const CATEGORY_BY_KEY = Object.fromEntries(BASELINE_CATEGORY_DEFS.map((c) => [c.key, c]));

/** Principal-sized Fin. Cost rows are almost always mis-tagged facility cash. */
const FINANCING_PRINCIPAL_MIN = 50_000;

function clone(value) {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
}

function asNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function asString(value, fallback = '') {
  return typeof value === 'string' ? value.trim() : fallback;
}

export function newEntityId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `ent${crypto.randomUUID().replace(/-/g, '').slice(0, 10)}`;
  }
  return `ent${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function newCashItemId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `csh${crypto.randomUUID().replace(/-/g, '').slice(0, 10)}`;
  }
  return `csh${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function todayIsoDate(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parse a cash-tracker calendar date to YYYY-MM-DD.
 * Accepts ISO dates, ISO datetimes, and DD.MM.YYYY / DD/MM/YYYY.
 * Returns null when the value is missing/invalid — never invents "today".
 */
export function parseCashIsoDate(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  const fromParts = (y, m, d) => {
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return null;
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };

  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (iso) return fromParts(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const dmy = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (dmy) return fromParts(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]));

  return null;
}

/**
 * Format a stored cash date for display / typing as DD.MM.YYYY.
 * Invalid or empty values return '' — never invents "today".
 */
export function formatCashDisplayDate(raw) {
  const iso = parseCashIsoDate(raw);
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * True when `raw` is a typed cash display date (D.M.YYYY / DD.MM.YYYY),
 * not an ISO string. Used by the inline editor save path so agents follow
 * the documented DD.MM.YYYY workflow.
 */
export function isCashDisplayDateInput(raw) {
  const s = String(raw ?? '').trim();
  if (!/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(s)) return false;
  return parseCashIsoDate(s) != null;
}

export function addDaysIso(isoDate, days) {
  const [y, m, d] = String(isoDate || todayIsoDate()).split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() + days);
  return todayIsoDate(dt);
}

export function categoryDef(key) {
  return CATEGORY_BY_KEY[key] || CATEGORY_BY_KEY.other;
}

export function isLaunched(cashTracker) {
  return !!(cashTracker && cashTracker.approvedBaseline && Array.isArray(cashTracker.approvedBaseline.categories));
}

export function buildApprovedBaseline({
  outputs = {},
  scenario = {},
  currency = 'CHF',
  approvedAt,
} = {}) {
  const categories = BASELINE_CATEGORY_DEFS.map((def) => ({
    key: def.key,
    label: def.label,
    section: def.section,
    kind: def.kind,
    // Funding has no P&L budget; other is ledger-only adjustments.
    budget: def.kind === 'funding' || def.key === 'other' ? 0 : asNumber(outputs[def.key]),
  }));
  const costBudget = categories
    .filter((c) => c.kind === 'cost')
    .reduce((sum, c) => sum + asNumber(c.budget), 0);

  return {
    scenarioId: asString(scenario.id),
    scenarioName: asString(scenario.name, 'Base case'),
    scenarioOrdinal: Number.isFinite(Number(scenario.ordinal)) ? Number(scenario.ordinal) : 1,
    approvedAt: approvedAt || new Date().toISOString(),
    currency: asString(currency, 'CHF').toUpperCase() || 'CHF',
    categories,
    totals: snapshotBaselineTotals(outputs, categories, costBudget),
  };
}

function finiteOr(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function snapshotBaselineTotals(outputs, categories, costBudget) {
  const src = outputs || {};
  const byKey = Object.fromEntries((categories || []).map((c) => [c.key, asNumber(c.budget)]));
  const totalRevenue = finiteOr(src.totalRevenue, asNumber(byKey.salesRevenue) + asNumber(byKey.parkingRevenue));
  const totalDevCost = finiteOr(src.totalDevCost, DEV_COST_KEYS.reduce((s, k) => s + asNumber(byKey[k]), 0));
  const taxAmount = finiteOr(src.taxAmount, asNumber(byKey.taxAmount));
  const totalFinancingCost = finiteOr(src.totalFinancingCost, asNumber(byKey.totalFinancingCost));
  const profitBeforeTax = finiteOr(src.profitBeforeTax, totalRevenue - totalDevCost);
  const profitAfterTax = finiteOr(src.profitAfterTax, profitBeforeTax - taxAmount);
  const netAfterFinancing = finiteOr(src.netAfterFinancing, profitAfterTax - totalFinancingCost);
  const finalNetProfit = finiteOr(src.finalNetProfit, netAfterFinancing);
  // SSOT matches calculation_engine: margin = PAT / revenue (not Net / revenue)
  const marginPercent = finiteOr(src.marginPercent, totalRevenue ? profitAfterTax / totalRevenue : 0);
  return {
    totalRevenue,
    totalDevCost,
    taxAmount,
    totalFinancingCost,
    profitBeforeTax,
    profitAfterTax,
    netAfterFinancing,
    finalNetProfit,
    marginPercent,
    taxRate: asNumber(src.taxRate),
    costBudget,
  };
}

function hydrateBaselineTotals(rawTotals, categories, costBudget) {
  return snapshotBaselineTotals({
    ...rawTotals,
    totalRevenue: rawTotals?.totalRevenue,
    totalDevCost: rawTotals?.totalDevCost,
    taxAmount: rawTotals?.taxAmount,
    totalFinancingCost: rawTotals?.totalFinancingCost,
    profitBeforeTax: rawTotals?.profitBeforeTax,
    profitAfterTax: rawTotals?.profitAfterTax,
    netAfterFinancing: rawTotals?.netAfterFinancing,
    finalNetProfit: rawTotals?.finalNetProfit,
    marginPercent: rawTotals?.marginPercent,
    taxRate: rawTotals?.taxRate,
  }, categories, asNumber(rawTotals?.costBudget) || costBudget);
}

export function normalizeEntity(raw, fallback = 'Development') {
  const name = asString(raw?.name ?? raw?.entity, fallback) || fallback;
  return {
    id: asString(raw?.id) || newEntityId(),
    name,
    role: ENTITY_ROLES.includes(raw?.role) ? raw.role : 'other',
  };
}

export function uniqueEntityName(entities, wanted, fallback = 'Entity') {
  const base = asString(wanted, fallback) || fallback;
  const taken = new Set((entities || []).map((e) => String(e.name || '').toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  let n = 2;
  while (taken.has(`${base} ${n}`.toLowerCase())) n += 1;
  return `${base} ${n}`;
}

export function ensureEntities(cashTracker, fallback = 'Development') {
  const current = cashTracker && typeof cashTracker === 'object' ? cashTracker : null;
  const byKey = new Map();
  const add = (raw) => {
    const name = asString(raw?.name ?? raw?.entity, fallback) || fallback;
    const key = name.toLowerCase();
    if (byKey.has(key)) return;
    byKey.set(key, normalizeEntity({ ...raw, name }, fallback));
  };
  (current?.entities || []).forEach(add);
  (current?.openingBalances || []).forEach((row) => add({ name: row?.entity }));
  (current?.items || []).forEach((row) => add({ name: row?.entity }));
  if (!byKey.size) add({ name: fallback, role: 'spv' });
  return [...byKey.values()];
}

export function createCashTracker({
  baseline,
  defaultEntity = 'Development',
  launchedAt,
} = {}) {
  if (!baseline) return null;
  const entity = asString(defaultEntity, 'Development') || 'Development';
  const registry = [normalizeEntity({ name: entity, role: 'spv' })];
  return {
    launchedAt: launchedAt || baseline.approvedAt || new Date().toISOString(),
    approvedBaseline: clone(baseline),
    entities: registry,
    openingBalances: [{ entity, amount: 0 }],
    designatedReserves: 0,
    items: [],
  };
}

function normalizeCategory(raw) {
  const def = categoryDef(raw?.key);
  const allowed = ['revenue', 'cost', 'other', 'funding'];
  return {
    key: def.key,
    label: asString(raw?.label, def.label),
    section: asString(raw?.section, def.section),
    kind: allowed.includes(raw?.kind) ? raw.kind : def.kind,
    budget: def.kind === 'funding' ? 0 : asNumber(raw?.budget),
  };
}

/** Ensure launched baselines pick up new funding categories without losing budgets. */
export function ensureBaselineCategories(categories) {
  const byKey = new Map((categories || []).map((row) => [row.key, normalizeCategory(row)]));
  return BASELINE_CATEGORY_DEFS.map((def) => {
    if (byKey.has(def.key)) {
      const existing = byKey.get(def.key);
      // Keep stored budgets for P&L leaves; force funding budget to 0 and refresh labels.
      if (def.kind === 'funding') {
        return { ...existing, label: def.label, section: def.section, kind: 'funding', budget: 0 };
      }
      if (def.key === 'totalFinancingCost') {
        return { ...existing, label: def.label, section: def.section, kind: def.kind };
      }
      return existing;
    }
    return normalizeCategory({ key: def.key, budget: 0 });
  });
}

/**
 * Facility principal was often tagged as Fin. Cost, which made fees budget (~20k)
 * look like a denomination mismatch vs draws/repays (~1.7M). Reclass those rows.
 */
export function reclassifyFinancingPrincipalItems(items, categories) {
  const finBudget = asNumber(
    (categories || []).find((row) => row.key === 'totalFinancingCost')?.budget,
  );
  const principalFloor = Math.max(FINANCING_PRINCIPAL_MIN, finBudget * 5);
  return (items || []).map((item) => {
    if (item.categoryKey !== 'totalFinancingCost') return item;
    if (item.direction === 'in') {
      return { ...item, categoryKey: 'financingDraw' };
    }
    if (asNumber(item.amount) >= principalFloor) {
      return { ...item, categoryKey: 'financingRepayment' };
    }
    return item;
  });
}

export function isFundingCategory(key) {
  return FUNDING_KEYS.includes(key) || categoryDef(key).kind === 'funding';
}

export function normalizeCashItem(raw, { previousDate = null } = {}) {
  const direction = CASH_DIRECTIONS.includes(raw?.direction) ? raw.direction : 'out';
  const status = CASH_STATUSES.includes(raw?.status) ? raw.status : 'expected';
  const def = categoryDef(raw?.categoryKey);
  // Prefer an explicit valid date (including past). Only fall back to the
  // previous stored date, then today — never silently overwrite a past ISO date.
  const date =
    parseCashIsoDate(raw?.date)
    || parseCashIsoDate(previousDate)
    || todayIsoDate();
  return {
    id: asString(raw?.id) || newCashItemId(),
    date,
    direction,
    entity: asString(raw?.entity, 'Development') || 'Development',
    counterparty: asString(raw?.counterparty),
    categoryKey: def.key,
    amount: Math.abs(asNumber(raw?.amount)),
    status,
    note: asString(raw?.note),
    documentId: asString(raw?.documentId) || null,
    documentName: asString(raw?.documentName) || null,
  };
}

export function hydrateCashTracker(raw) {
  if (!raw || typeof raw !== 'object' || !raw.approvedBaseline) return null;
  const cats = ensureBaselineCategories(
    Array.isArray(raw.approvedBaseline.categories)
      ? raw.approvedBaseline.categories
      : buildApprovedBaseline({ outputs: {} }).categories,
  );
  const costBudget = cats.filter((c) => c.kind === 'cost').reduce((sum, c) => sum + asNumber(c.budget), 0);
  const opening = Array.isArray(raw.openingBalances) && raw.openingBalances.length
    ? raw.openingBalances.map((row) => ({
      entity: asString(row?.entity, 'Development') || 'Development',
      amount: asNumber(row?.amount),
    }))
    : [{ entity: 'Development', amount: 0 }];

  const normalizedItems = Array.isArray(raw.items) ? raw.items.map(normalizeCashItem) : [];
  const items = reclassifyFinancingPrincipalItems(normalizedItems, cats);

  return {
    launchedAt: raw.launchedAt || raw.approvedBaseline.approvedAt || null,
    approvedBaseline: {
      scenarioId: asString(raw.approvedBaseline.scenarioId),
      scenarioName: asString(raw.approvedBaseline.scenarioName, 'Base case'),
      scenarioOrdinal: Number(raw.approvedBaseline.scenarioOrdinal) || 1,
      approvedAt: raw.approvedBaseline.approvedAt || raw.launchedAt || null,
      currency: asString(raw.approvedBaseline.currency, 'CHF').toUpperCase() || 'CHF',
      categories: cats,
      totals: hydrateBaselineTotals(raw.approvedBaseline.totals, cats, costBudget),
    },
    openingBalances: opening,
    designatedReserves: Math.max(0, asNumber(raw.designatedReserves)),
    items,
    entities: ensureEntities({
      entities: raw.entities,
      openingBalances: opening,
      items,
    }, opening[0]?.entity || 'Development'),
  };
}

/** Server-side: once launched, keep the stored baseline; allow ledger edits only. */
export function mergeCashTracker(existing, incoming) {
  const next = hydrateCashTracker(incoming);
  const prev = hydrateCashTracker(existing);
  if (!next && !prev) return null;
  if (!prev) return next;
  if (!next) return prev;
  return {
    ...next,
    launchedAt: prev.launchedAt,
    approvedBaseline: clone(prev.approvedBaseline),
  };
}

export function upsertCashItem(cashTracker, item) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return cashTracker;
  const existing = current.items.find((row) => row.id === item?.id);
  const normalized = normalizeCashItem(item, { previousDate: existing?.date || null });
  const nextItem = reclassifyFinancingPrincipalItems(
    [normalized],
    current.approvedBaseline.categories,
  )[0];
  const idx = current.items.findIndex((row) => row.id === nextItem.id);
  const items = idx >= 0
    ? current.items.map((row, i) => (i === idx ? nextItem : row))
    : [...current.items, nextItem];
  return { ...current, items };
}

export function removeCashItem(cashTracker, id) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return cashTracker;
  return { ...current, items: current.items.filter((row) => row.id !== id) };
}

/** Copy a ledger row with a new id. Attachment refs are kept; the source row is unchanged. */
export function duplicateCashItem(cashTracker, source) {
  const current = hydrateCashTracker(cashTracker);
  const raw = source && typeof source === 'object'
    ? source
    : current?.items.find((row) => row.id === source);
  if (!current || !raw) return current || cashTracker;
  const copy = normalizeCashItem({
    ...raw,
    id: newCashItemId(),
  });
  if (copy.id === raw.id) {
    copy.id = `csh${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  }
  return { ...current, items: [...current.items, copy] };
}

export function setOpeningBalances(cashTracker, openingBalances) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return cashTracker;
  const rows = (openingBalances || []).map((row) => ({
    entity: asString(row?.entity, 'Development') || 'Development',
    amount: asNumber(row?.amount),
  }));
  const nextOpening = rows.length ? rows : [{ entity: 'Development', amount: 0 }];
  const next = { ...current, openingBalances: nextOpening };
  return { ...next, entities: ensureEntities(next) };
}

export function setDesignatedReserves(cashTracker, amount) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return cashTracker;
  return { ...current, designatedReserves: Math.max(0, asNumber(amount)) };
}

export function listEntities(cashTracker, fallback = 'Development') {
  const current = hydrateCashTracker(cashTracker);
  return ensureEntities(current, fallback).map((row) => row.name);
}

function openingAmountFor(current, name) {
  return (current?.openingBalances || [])
    .filter((row) => row.entity === name)
    .reduce((sum, row) => sum + asNumber(row.amount), 0);
}

export function addEntity(cashTracker, { name = 'Entity', role = 'other' } = {}) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return cashTracker;
  const nextName = uniqueEntityName(current.entities, name);
  const entity = normalizeEntity({ name: nextName, role });
  return {
    ...current,
    entities: [...current.entities, entity],
    openingBalances: [...current.openingBalances, { entity: nextName, amount: 0 }],
  };
}

export function renameEntity(cashTracker, id, name) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return cashTracker;
  const target = current.entities.find((row) => row.id === id);
  const nextName = asString(name);
  if (!target || !nextName || nextName === target.name) return current;
  const clash = current.entities.some((row) => row.id !== id && row.name.toLowerCase() === nextName.toLowerCase());
  const finalName = clash ? uniqueEntityName(current.entities, nextName) : nextName;
  const old = target.name;
  return {
    ...current,
    entities: current.entities.map((row) => (row.id === id ? { ...row, name: finalName } : row)),
    openingBalances: current.openingBalances.map((row) => (row.entity === old ? { ...row, entity: finalName } : row)),
    items: current.items.map((row) => (row.entity === old ? { ...row, entity: finalName } : row)),
  };
}

export function setEntityRole(cashTracker, id, role) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return cashTracker;
  const nextRole = ENTITY_ROLES.includes(role) ? role : 'other';
  return {
    ...current,
    entities: current.entities.map((row) => (row.id === id ? { ...row, role: nextRole } : row)),
  };
}

export function setEntityOpening(cashTracker, id, amount) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return cashTracker;
  const target = current.entities.find((row) => row.id === id);
  if (!target) return current;
  const others = current.openingBalances.filter((row) => row.entity !== target.name);
  return {
    ...current,
    openingBalances: [...others, { entity: target.name, amount: asNumber(amount) }],
  };
}

export function removeEntity(cashTracker, id) {
  const current = hydrateCashTracker(cashTracker);
  if (!current || current.entities.length <= 1) return current;
  const target = current.entities.find((row) => row.id === id);
  const fallback = current.entities.find((row) => row.id !== id);
  if (!target || !fallback) return current;
  const moved = openingAmountFor(current, target.name);
  const keepOpening = current.openingBalances.filter((row) => row.entity !== target.name);
  const fallbackIdx = keepOpening.findIndex((row) => row.entity === fallback.name);
  const openingBalances = fallbackIdx >= 0
    ? keepOpening.map((row, i) => (i === fallbackIdx ? { ...row, amount: asNumber(row.amount) + moved } : row))
    : [...keepOpening, { entity: fallback.name, amount: moved }];
  return {
    ...current,
    entities: current.entities.filter((row) => row.id !== id),
    openingBalances,
    items: current.items.map((row) => (row.entity === target.name ? { ...row, entity: fallback.name } : row)),
  };
}

export function summarizeByEntity(cashTracker, { today = todayIsoDate() } = {}) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return [];
  return current.entities.map((entity) => {
    const opening = openingAmountFor(current, entity.name);
    let cashIn = 0;
    let cashOut = 0;
    let unpaidSigned = 0;
    let dueOutflows = 0;
    current.items.forEach((item) => {
      if (item.entity !== entity.name || item.status === 'cancelled') return;
      if (item.status === 'paid') {
        if (item.direction === 'in') cashIn += item.amount;
        else cashOut += item.amount;
      }
      if (item.direction === 'out' && item.status === 'due') dueOutflows += item.amount;
      // Full-ledger unpaid forecast — not a rolling 90-day window.
      if (isUnpaid(item)) unpaidSigned += signedAmount(item);
    });
    const cashToday = opening + cashIn - cashOut;
    const futureCash = cashToday + unpaidSigned;
    return {
      ...entity,
      opening,
      cashIn,
      cashOut,
      cashToday,
      futureCash,
      dueOutflows,
      cashTodayNegative: cashToday < 0,
    };
  });
}

function isUnpaid(item) {
  return item.status === 'expected' || item.status === 'due';
}

function signedAmount(item) {
  const amt = asNumber(item.amount);
  return item.direction === 'in' ? amt : -amt;
}

/**
 * Headline liquidity KPIs.
 * - cashIn / cashOut / cashToday: paid movements only (money actually held).
 * - futureCash: cashToday ± every non-cancelled expected/due item on the full
 *   ledger (not a 90-day window). Forecast only — not spendable today.
 * - deployableCash: cashToday − due outflows − designated reserves.
 *   Never includes expected inflows.
 * remainingBudget stays for budget/recon detail, not the top KPI strip.
 */
export function summarizeCash(cashTracker, { today = todayIsoDate() } = {}) {
  const current = hydrateCashTracker(cashTracker);
  const empty = {
    opening: 0,
    cashIn: 0,
    cashOut: 0,
    cashToday: 0,
    futureCash: 0,
    remainingBudget: 0,
    costBudget: 0,
    trackedOutflows: 0,
    dueOutflows: 0,
    designatedReserves: 0,
    deployableCash: 0,
    cashTodayNegative: false,
    currency: 'CHF',
    launched: false,
  };
  if (!current) return empty;

  const opening = current.openingBalances.reduce((sum, row) => sum + asNumber(row.amount), 0);
  let cashIn = 0;
  let cashOut = 0;
  let unpaidSigned = 0;
  let dueOutflows = 0;
  let trackedOutflows = 0;

  current.items.forEach((item) => {
    if (item.status === 'cancelled') return;
    if (item.status === 'paid') {
      if (item.direction === 'in') cashIn += item.amount;
      else cashOut += item.amount;
    }
    if (item.direction === 'out') {
      // Principal repayments are funding, not P&L cost-budget spend.
      if (!isFundingCategory(item.categoryKey)) trackedOutflows += item.amount;
      if (item.status === 'due') dueOutflows += item.amount;
    }
    if (isUnpaid(item)) {
      unpaidSigned += signedAmount(item);
    }
  });

  const costBudget = asNumber(current.approvedBaseline.totals?.costBudget);
  const remainingBudget = costBudget - trackedOutflows;
  const cashToday = opening + cashIn - cashOut;
  const futureCash = cashToday + unpaidSigned;
  const designatedReserves = current.designatedReserves;
  // Deployable is held cash after due obligations and reserves — never expected.
  const deployableCash = cashToday - dueOutflows - designatedReserves;

  return {
    opening,
    cashIn,
    cashOut,
    cashToday,
    futureCash,
    remainingBudget,
    costBudget,
    trackedOutflows,
    dueOutflows,
    designatedReserves,
    deployableCash,
    cashTodayNegative: cashToday < 0,
    currency: current.approvedBaseline.currency || 'CHF',
    launched: true,
    baselineName: current.approvedBaseline.scenarioName,
    launchedAt: current.launchedAt,
  };
}

export function reconcileBudget(cashTracker) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return [];

  return current.approvedBaseline.categories.map((cat) => {
    let paid = 0;
    let open = 0;
    // Revenue + financing draws count inflows; costs/fees/repayments count outflows.
    const countIn = cat.kind === 'revenue' || cat.key === 'financingDraw';
    current.items.forEach((item) => {
      if (item.categoryKey !== cat.key || item.status === 'cancelled') return;
      if (countIn ? item.direction !== 'in' : item.direction !== 'out') return;
      if (item.status === 'paid') paid += item.amount;
      else open += item.amount;
    });
    const forecasted = paid + open;
    const remaining = asNumber(cat.budget) - forecasted;
    const overBudget = cat.kind === 'cost' && asNumber(cat.budget) > 0
      ? forecasted > asNumber(cat.budget)
      : cat.kind === 'cost' && forecasted > asNumber(cat.budget) && forecasted > 0;
    return {
      ...cat,
      paidOut: paid,
      openOut: open,
      paid,
      open,
      forecasted,
      remaining,
      overBudget,
    };
  });
}

function emptyMoney() {
  return { budget: 0, paid: 0, open: 0, forecasted: 0, remaining: 0, overBudget: false };
}

function sumMoney(rows) {
  return (rows || []).reduce((acc, row) => ({
    budget: acc.budget + asNumber(row.budget),
    paid: acc.paid + asNumber(row.paid ?? row.paidOut),
    open: acc.open + asNumber(row.open ?? row.openOut),
    forecasted: acc.forecasted + asNumber(row.forecasted),
    remaining: acc.remaining + asNumber(row.remaining),
    overBudget: acc.overBudget || !!row.overBudget,
  }), emptyMoney());
}

function subtractMoney(left, right) {
  return {
    budget: asNumber(left.budget) - asNumber(right.budget),
    paid: asNumber(left.paid) - asNumber(right.paid),
    open: asNumber(left.open) - asNumber(right.open),
    forecasted: asNumber(left.forecasted) - asNumber(right.forecasted),
    remaining: asNumber(left.remaining) - asNumber(right.remaining),
    overBudget: false,
  };
}

function pickRows(byKey, keys) {
  return keys.map((key) => byKey[key]).filter(Boolean);
}

/**
 * Budget vs cash in the same line order as Evaluation:
 * Revenue → cost breakdown (incl. Sales Cost) → Profit (PBT, Tax, PAT, Fin, Net).
 */
export function evaluationReconcile(cashTracker) {
  const current = hydrateCashTracker(cashTracker);
  if (!current) return null;
  const leaves = reconcileBudget(current);
  const byKey = Object.fromEntries(leaves.map((row) => [row.key, row]));
  const totals = current.approvedBaseline.totals || {};

  const moneyRow = (key, label, money, extra = {}) => ({
    key,
    label,
    kind: extra.kind || 'line',
    ...emptyMoney(),
    ...money,
    overBudget: extra.overBudget || money.overBudget || false,
    format: extra.format || 'money',
    indent: extra.indent || false,
    testId: extra.testId || `cash-recon-${key}`,
  });

  const leafRow = (key, label) => {
    const src = byKey[key] || { key, label, ...emptyMoney() };
    return moneyRow(key, label || src.label, src, {
      kind: src.kind || 'cost',
      indent: true,
      overBudget: src.overBudget,
    });
  };

  const revenueLeaves = pickRows(byKey, REVENUE_KEYS);
  const landLeaves = pickRows(byKey, LAND_KEYS);
  const buildLeaves = pickRows(byKey, CONSTRUCTION_KEYS);
  const softLeaves = pickRows(byKey, SOFT_KEYS);
  const commercialLeaves = pickRows(byKey, COMMERCIAL_KEYS);
  const revenue = sumMoney(revenueLeaves);
  const land = sumMoney(landLeaves);
  const build = sumMoney(buildLeaves);
  const soft = sumMoney(softLeaves);
  const commercial = sumMoney(commercialLeaves);
  const devCost = sumMoney([...landLeaves, ...buildLeaves, ...softLeaves, ...commercialLeaves]);
  const tax = byKey.taxAmount || { ...emptyMoney(), budget: asNumber(totals.taxAmount) };
  const fin = byKey.totalFinancingCost || { ...emptyMoney(), budget: asNumber(totals.totalFinancingCost) };

  const pbt = {
    ...subtractMoney(revenue, devCost),
    budget: finiteOr(totals.profitBeforeTax, revenue.budget - devCost.budget),
  };
  pbt.remaining = pbt.budget - pbt.forecasted;
  const pat = {
    ...subtractMoney(pbt, tax),
    budget: finiteOr(totals.profitAfterTax, pbt.budget - asNumber(tax.budget)),
  };
  pat.remaining = pat.budget - pat.forecasted;
  const net = {
    ...subtractMoney(pat, fin),
    budget: finiteOr(totals.finalNetProfit ?? totals.netAfterFinancing, pat.budget - asNumber(fin.budget)),
  };
  net.remaining = net.budget - net.forecasted;
  const revenueBudget = revenue.budget || asNumber(totals.totalRevenue);
  // SSOT: Margin = PAT / Revenue (same as evaluation P&L / Deal Quality)
  const marginBudget = finiteOr(totals.marginPercent, revenueBudget ? pat.budget / revenueBudget : 0);
  const marginRealized = revenue.forecasted ? pat.forecasted / revenue.forecasted : null;

  const taxLabel = asNumber(totals.taxRate) > 0
    ? `Tax ${(asNumber(totals.taxRate) * 100).toFixed(1)}%`
    : (byKey.taxAmount?.label || 'Tax');

  const rows = [
    { key: 'sec-revenue', kind: 'section', label: '1 · Revenue' },
    leafRow('salesRevenue', 'Sales'),
    leafRow('parkingRevenue', 'Parking'),
    moneyRow('totalRevenue', 'TOTAL REVENUE', { ...revenue, budget: finiteOr(totals.totalRevenue, revenue.budget) }, { kind: 'total' }),

    { key: 'sec-costs', kind: 'section', label: '2 · Cost breakdown' },
    { key: 'grp-land', kind: 'group', label: 'Land' },
    leafRow('landCost', 'Terrain'),
    leafRow('notaryFees', 'Notary'),
    leafRow('otherLandFees', 'Other'),
    moneyRow('landTotal', 'Land Total', land, { kind: 'total' }),
    { key: 'grp-construction', kind: 'group', label: 'Construction' },
    leafRow('constructionCost', 'Build'),
    leafRow('constructionReserve', 'Reserve'),
    moneyRow('buildTotal', 'Build Total', build, { kind: 'total' }),
    { key: 'grp-soft', kind: 'group', label: 'Soft Costs' },
    leafRow('architectureCost', 'Architecture'),
    leafRow('consultantsCost', 'Consultants'),
    leafRow('contingencyCost', 'Contingency'),
    { key: 'grp-commercial', kind: 'group', label: 'Commercial' },
    leafRow('commercializationCost', 'Sales Cost'),
    leafRow('pilotageCost', 'Pilotage'),
    moneyRow('devCost', 'DEV COST', { ...devCost, budget: finiteOr(totals.totalDevCost, devCost.budget) }, { kind: 'total' }),

    { key: 'sec-profit', kind: 'section', label: 'Profit', tone: 'profit' },
    moneyRow('pbt', 'PBT', pbt, { kind: 'total', testId: 'cash-recon-pbt' }),
    moneyRow('taxAmount', taxLabel, tax, { kind: 'cost', indent: true, overBudget: tax.overBudget }),
    moneyRow('pat', 'PAT', pat, { kind: 'total', testId: 'cash-recon-pat' }),
    moneyRow('totalFinancingCost', 'Fin. Cost (fees)', fin, { kind: 'cost', indent: true, overBudget: fin.overBudget, testId: 'cash-recon-financing' }),
    moneyRow('net', 'Net', net, { kind: 'total', testId: 'cash-recon-net' }),
    moneyRow('margin', 'Margin', {
      budget: marginBudget,
      paid: null,
      open: null,
      forecasted: marginRealized,
      remaining: marginRealized,
    }, { kind: 'margin', format: 'percent', testId: 'cash-recon-margin' }),
  ];

  const draws = byKey.financingDraw || emptyMoney();
  const repays = byKey.financingRepayment || emptyMoney();
  const fundingActive = asNumber(draws.forecasted) || asNumber(repays.forecasted);
  if (fundingActive) {
    const fundingNet = subtractMoney(draws, repays);
    rows.push({ key: 'sec-funding', kind: 'section', label: 'Funding (principal)' });
    rows.push(leafRow('financingDraw', 'Financing in'));
    rows.push(leafRow('financingRepayment', 'Financing out'));
    rows.push(moneyRow('fundingNet', 'Net funding', fundingNet, {
      kind: 'total',
      testId: 'cash-recon-funding-net',
    }));
  }

  const feasibility = byKey.feasibilityStudy;
  if (feasibility && (asNumber(feasibility.budget) || asNumber(feasibility.forecasted))) {
    rows.push({ key: 'grp-capital', kind: 'group', label: 'Capital' });
    rows.push(leafRow('feasibilityStudy', 'Feasibility'));
  }
  const other = byKey.other;
  if (other && asNumber(other.forecasted)) {
    rows.push(leafRow('other', 'Other / adjustment'));
  }

  return {
    currency: current.approvedBaseline.currency || 'CHF',
    rows,
    leaves,
  };
}

export function materialExceptions(cashTracker, { today } = {}) {
  const summary = summarizeCash(cashTracker, { today });
  const rows = reconcileBudget(cashTracker);
  const exceptions = [];
  if (summary.cashTodayNegative) {
    exceptions.push({
      type: 'negative_cash',
      label: 'Cash today is negative',
      amount: summary.cashToday,
    });
  }
  rows.forEach((row) => {
    if (row.overBudget) {
      exceptions.push({
        type: 'over_budget',
        key: row.key,
        label: `${row.label} exceeds baseline`,
        amount: row.forecasted - row.budget,
      });
    }
  });
  return exceptions;
}

/**
 * Compact cross-development rollup. Each deal: { id, name, cashTracker | cashSummary }.
 * Deployable never includes expected inflows or projected profit.
 * Future cash is the full-ledger unpaid forecast, not a 90-day window.
 */
export function portfolioCash(deals = [], { today } = {}) {
  const rows = [];
  let opening = 0;
  let cashIn = 0;
  let cashOut = 0;
  let cashToday = 0;
  let futureCash = 0;
  let dueOutflows = 0;
  let designatedReserves = 0;

  deals.forEach((deal) => {
    const tracker = deal?.cashTracker ? hydrateCashTracker(deal.cashTracker) : null;
    if (!tracker && !deal?.cashSummary?.launched) return;
    const summary = tracker
      ? summarizeCash(tracker, { today })
      : { ...deal.cashSummary, launched: true };
    if (!summary.launched) return;
    opening += asNumber(summary.opening);
    cashIn += asNumber(summary.cashIn);
    cashOut += asNumber(summary.cashOut);
    cashToday += asNumber(summary.cashToday);
    // Prefer futureCash; fall back for older compact payloads that still ship next90.
    futureCash += asNumber(
      summary.futureCash != null ? summary.futureCash : summary.next90,
    );
    dueOutflows += asNumber(summary.dueOutflows);
    designatedReserves += asNumber(summary.designatedReserves);
    rows.push({
      id: deal.id,
      name: deal.name || 'Untitled',
      ...summary,
      futureCash: asNumber(
        summary.futureCash != null ? summary.futureCash : summary.next90,
      ),
    });
  });

  return {
    opening,
    cashIn,
    cashOut,
    cashToday,
    futureCash,
    dueOutflows,
    designatedReserves,
    deployableCash: cashToday - dueOutflows - designatedReserves,
    cashTodayNegative: cashToday < 0,
    developments: rows,
  };
}

export function compactCashSummary(cashTracker, { today } = {}) {
  const summary = summarizeCash(cashTracker, { today });
  if (!summary.launched) return null;
  return {
    launched: true,
    opening: summary.opening,
    cashIn: summary.cashIn,
    cashOut: summary.cashOut,
    cashToday: summary.cashToday,
    futureCash: summary.futureCash,
    dueOutflows: summary.dueOutflows,
    designatedReserves: summary.designatedReserves,
    deployableCash: summary.deployableCash,
    // Budget detail for cards/recon — not a headline liquidity KPI.
    remainingBudget: summary.remainingBudget,
    currency: summary.currency,
    baselineName: summary.baselineName,
  };
}

export function emptyCashItem({
  entity = 'Development',
  categoryKey = 'constructionCost',
  direction = 'out',
  status = 'expected',
} = {}) {
  return {
    id: newCashItemId(),
    date: todayIsoDate(),
    direction,
    entity,
    counterparty: '',
    categoryKey,
    amount: 0,
    status,
    note: '',
    documentId: null,
    documentName: null,
  };
}

export { clone as cloneCashValue };
